/**
 * This wrapper makes it possible to open a two-way interactive communication session
 * between the user's browser and a server. With this API, you can send messages to a
 * server and receive event-driven responses without having to poll the server for a reply.
 *
 * @author Michel Heitbrink
 */

import EncryptionGPG from './encryption/gpg/Encryption';
import EncryptionRSA from './encryption/rsa-aes/Encryption';
import Plugin from './core/Plugin';

const Encryption = ( ENCRYPTION.gpg ) ? EncryptionGPG : EncryptionRSA;

class Socket extends Plugin  {

	static pluginName = 'Socket';

	static getDefaults() {

		return {
			autoconnect: false,
			secure: false,
			url: 'wss://' + window.location.host + ':8443',
		};
	}

	static getName() {
		return Socket.pluginName;
	}

	constructor( options ) {

		super(options);

		this.listeners = [];

		/**
		 * Adding events as listeners
		 */
		if ( typeof this.options.events == 'object' ) {

			Object.keys(this.options.events).forEach( (event) => {

				if ( typeof this.options.events[event] == '' ) {
					this.on(event, this.options.events[event]);
				}
			});
		}

		/**
		 * autoconnect the socket with the given host
		 */
		if ( typeof this.options.autoconnect == 'boolean' && this.options.autoconnect ) {
			this.autoconnect();
		}

		if ( this.options.secure === true ) {
			this.encryptor = new Encryption();
		}
	}

	/**
	 * Connect to the host
	 */
	autoconnect() {

		if ( typeof this.options.autoconnect == 'boolean' && this.options.autoconnect ) {

			if ( !this.isConnected() ) {
				this.connect();
			}
		}
	}

	/**
	 * Connect with a socket to the host.
	 * Also adding callbacks to the socket.
	 */
	connect() {

		let url = ( this.options.url ) ? this.options.url : 'wss://' + window.location.host + ':8443';

		this.connection = new WebSocket(url);

		this.connection.onopen = () => {
			this.trigger('open');
		};

		this.connection.onclose = () => {
			this.trigger('close');
		};

		this.connection.onmessage = (event) => {
			this.onMessage(event);
		};
	}

	/**
	 * Callback if the socket receives data. Based on the given event, listeners will be triggered.
	 *
	 * @param  {Event} event
	 */
	onMessage(event) {

		if ( event.data ) {

			let response = event.data;

			this.trigger('receive', response);

			/**
			 * If the the dataset is a string, try the parse it like json.
			 */
			if ( typeof event.data == 'string' ) {

				/**
				 * Decrypt result if secure option is set
				 */
				if ( this.options.secure === true ) {

					this.encryptor.decrypt( event.data, false ).then( ( decrypted ) => {

						response = JSON.parse(event.data);

						if ( response.event ) {
							this.trigger(response.event, response.data);
						}
					});
				}
				else {
					response = JSON.parse(event.data);
				}
			}

			/**
			 * Find listeners for received events
			 */
			if ( response.event && this.options.secure !== true ) {
				this.trigger(response.event, response.data);
			}
		}
	}

	/**
	 * Send data to host
	 *
	 * @param  {String|Object} data
	 */
	send(data) {

		if ( this.isOpen() ) {

			if ( data ) {

				if ( typeof data == 'object' ) {
					data = JSON.stringify(data);
				}

				/**
				 * Send data encrypted if secure option is set.
				 */
				if ( this.options.secure === true ) {

					this.encryptor.encrypt( event.data ).then( ( encrypted ) => {
						this.connection.send(encrypted);
					});

				} else {
					this.connection.send(data);
				}
			}
		}
		else {
			throw new Error('Connection is closed');
		}
	}

	/**
	 * Close connection
	 */
	close() {
		this.connection.close();
	}

	/**
	 * Trigger listeners based on event
	 *
	 * @param  {String} event
	 * @param  {Object} data
	 */
	trigger(event, data) {

		this.listeners.forEach( ( action ) => {

			if ( action.event == event ) {

				action.listener(data);
			}
		});
	}

	/**
	 * Add function to the list of listeners. If the given eventname is equal to the received eventname, the function will be triggered.
	 *
	 * @param {String} event
	 * @param {Function} listener
	 */
	on(event, listener) {

		if ( typeof listener == 'function' ) {

			this.listeners.push({
				event,
				listener
			});
		}
	}

	/**
	 * Get the readyState of the websocket
	 * https://developer.mozilla.org/en-US/docs/Web/API/WebSocket/readyState
	 *
	 * @return {Number}
	 */
	getState() {
		return this.connection.readyState;
	}

	/**
	 * Check if the connection with the websocket is set up.
	 *
	 * @return {Boolean}
	 */
	isConnected() {
		return ( this.connection instanceof WebSocket );
	}

	/**
	 * Check if connection of the socket is closed
	 *
	 * @return {Boolean}
	 */
	isClosed() {
		return  this.isConnected() && ( this.connection.readyState == WebSocket.CLOSING || this.connection.readyState == WebSocket.CLOSED );
	}

	/**
	 * Check if connection of the socket is open
	 *
	 * @return {Boolean}
	 */
	isOpen() {
		return this.isConnected() && this.connection.readyState == WebSocket.Open;
	}
}

export default Socket;
/*
---
name: Socket
category: JavaScript
---
WebSocket wrapper to create a two-way interactive communication session between the user's browser and a server.

Constructor
======
The options are based on the Nutshell.options.Socket object.

##### Arguments:
- **Options** (Object) - List of options

##### Options
- **autoconnect** (Boolean) - Autoconnect with server
- **secure** (String) - Encrypt sending packages and decrypt receiving packages. Default: false
- **url** (String) - The absolute URL of the WebSocket. Default: 'wss://' + window.location.host + ':8443'

```js
import { Socket } from '@nutshell/core';

const socket = new Socket({
	request: {
		autoconnect: false,
		secure: false,
		events: {
			'open': () => { console.log('socket is open') },
			'close': () => { console.log('socket is closed') },
			'receive': (data) => { console.log('received raw data: ' + data ) }
		},
		url: 'wss://' + window.location.host + ':8443'
	}
});
```

Events
======
If your using the structure { 'event': 'example', 'data': 'hello world' } as response of the server, you can use the the `on` method to set up a function that will be called whenever the specified event is delivered

```js
socket.on('example', (data) => {
	console.log(data); //log 'hello world';
})
```

Status
======
Check the status of the connection with the following functions:

```js
socket.isConnected();
socket.isOpen();
socket.isClosed();

socket.getState(); //Returns the readyState of the WebSocket (https://developer.mozilla.org/en-US/docs/Web/API/WebSocket/readyState)
```
*/